import { InfoConfigInput, KEY_ROOM_NAME, KEY_ROOM_USAGE, Room } from "@/data/models/ProjectDetails";

/** The room usage key used before `Raum0020`; IRES Project still falls back to it. */
export const KEY_ROOM_USAGE_OLD = "Raum-Raumnutzung";

/**
 * The room's latest entries: once a drying extension recorded its own, they replace the damage
 * assessment's, as IRES Project reads them.
 */
export function getLatestRoomInfoInput(room: Room): InfoConfigInput | undefined {
  return room.dryingExtensionData?.infoInput ?? room.infoInput;
}

/**
 * The text of a free-text usage, stored as a selected option such as
 * `{"id":"15","isSelected":true,"quantity":"Waschküche "}`.
 *
 * @returns the trimmed text, or `null` for anything unreadable
 */
function parseFreeTextRoomUsage(roomUsage: string): string | null {
  if (!roomUsage.startsWith("{")) {
    return null;
  }

  try {
    const quantity = JSON.parse(roomUsage)?.quantity;

    return typeof quantity === "string" && quantity.trim() !== "" ? quantity.trim() : null;
  } catch {
    // Malformed input is shown as the bare room number rather than breaking the label.
    return null;
  }
}

/**
 * What a room is called beyond its number: the entered room name, else the room usage.
 *
 * A usage picked from the list is stored as its position in the project's Beschreibung, so it is
 * read against that version's options; a free-text usage carries its own text.
 *
 * @param room the room
 * @param usageOptions the room usage options of the project's pinned Beschreibung version, see
 * `getRoomUsageOptions`; `null` while they load, which leaves a picked usage unresolved
 * @returns the description, or `null` when the room has none that can be shown
 */
export function getRoomDescription(room?: Room, usageOptions: string[] | null = null): string | null {
  const input = room ? getLatestRoomInfoInput(room) : undefined;

  if (!input) {
    return null;
  }

  const roomName = input[KEY_ROOM_NAME]?.[0]?.trim();

  if (roomName) {
    return roomName;
  }

  const roomUsage = (input[KEY_ROOM_USAGE] ?? input[KEY_ROOM_USAGE_OLD])?.[0];

  if (!roomUsage) {
    return null;
  }

  // A position the version does not have yields no description, as in IRES Project.
  return /^\d+$/.test(roomUsage)
    ? usageOptions?.[Number(roomUsage)] || null
    : parseFreeTextRoomUsage(roomUsage);
}

/**
 * Joins the numbered room and its description into "Raum 3: Küche", as IRES Project's
 * `formatRoomName` does: the bare number when there is no description or it only repeats the
 * number, and a description already starting with the number is kept as it is.
 *
 * @param numberedRoom the localized "Raum n"
 * @param description see {@link getRoomDescription}
 */
export function joinRoomLabel(numberedRoom: string, description: string | null): string {
  if (!description || description === numberedRoom) {
    return numberedRoom;
  }

  if (description.startsWith(numberedRoom)) {
    return description;
  }

  return `${numberedRoom}: ${description}`;
}
